import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import CommonButton from "../elments/CommonButton";
import config from "../configs/config";

const QuestionPreview = ({ 
  question, 
  questionNumber, 
  surveyData,
  questionnaireFileId,
  surveyFileName,
  onShowToastMessage,
  onSetProcessingMessage
}) => {
  // ─────────────────────────────────────────────────────────────
  // ALL HOOKS FIRST
  // ─────────────────────────────────────────────────────────────
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState("split-docx"); // Default to split with DOCX
  const [splitRatio, setSplitRatio] = useState(55); // For survey/questionnaire split
  const [isDragging, setIsDragging] = useState(false);
  
  // DOCX Preview States
  const [embedUrl, setEmbedUrl] = useState(null);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [docxSplitRatio, setDocxSplitRatio] = useState(50); // For survey/docx split
  const [isDocxDragging, setIsDocxDragging] = useState(false);
  const [docxLoadError, setDocxLoadError] = useState(null);

  const containerRef = useRef(null);
  const docxContainerRef = useRef(null);

  // Debug props
  useEffect(() => {
    console.log("QuestionPreview Props:", {
      questionnaireFileId,
      surveyFileName,
      hasDocx: surveyFileName?.toLowerCase().endsWith(".docx"),
      questionNumber,
      hasSurveyData: !!surveyData
    });
  }, [questionnaireFileId, surveyFileName, questionNumber, surveyData]);

  // Ordered questions
  const allQuestions = surveyData ? Object.values(surveyData)
    .filter((q) => q && q._order !== undefined)
    .sort((a, b) => a._order - b._order) : [];

  const startIndex = allQuestions.findIndex(
    (q) => q.Question_number === questionNumber
  );
  const initialIndex = startIndex >= 0 ? startIndex : 0;

  useEffect(() => {
    if (isPreviewOpen) {
      setCurrentIndex(initialIndex);
      // Auto-load DOCX when preview opens if we have a file
      if (questionnaireFileId && surveyFileName?.toLowerCase().endsWith(".docx") && !embedUrl) {
        loadDocxPreview();
      }
    }
  }, [isPreviewOpen, initialIndex, questionnaireFileId, surveyFileName]);

  const currentQuestion = allQuestions[currentIndex] || question;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= allQuestions.length - 1;

  const handleNext = () => !isLast && setCurrentIndex((prev) => prev + 1);
  const handleBack = () => !isFirst && setCurrentIndex((prev) => prev - 1);

  const showToastMessage = (message) => {
    if (onShowToastMessage) {
      onShowToastMessage(message);
    } else {
      alert(message);
    }
  };

  const setProcessingMessage = (message) => {
    if (onSetProcessingMessage) {
      onSetProcessingMessage(message);
    }
  };

  // Load DOCX preview
  const loadDocxPreview = async () => {
    if (!questionnaireFileId) {
      console.error("No questionnaireFileId provided");
      setDocxLoadError("No document ID available");
      showToastMessage("No document available for preview");
      return;
    }

    try {
      setIsLoadingDocx(true);
      setDocxLoadError(null);
      setProcessingMessage("Loading document preview...");
      
      console.log("Loading DOCX with ID:", questionnaireFileId);
      console.log("Survey filename:", surveyFileName);

      const payload = {
        onedrive_file_id: questionnaireFileId,
        surveyFileName: surveyFileName,
      };

      console.log("Sending payload to /get_embed_url:", payload);

      const response = await fetch(`${config.API_BASE_URL}/get_embed_url`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload)
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(`Failed to get embed URL: ${response.status}`);
      }

      const data = await response.json();
      console.log("Response data:", data);

      if (data.success && data.embed_url) {
        console.log("Setting embed URL:", data.embed_url);
        setEmbedUrl(data.embed_url);
      } else {
        setDocxLoadError("Failed to load document preview");
        showToastMessage("Failed to load document preview");
      }

    } catch (error) {
      console.error("Error previewing document:", error);
      setDocxLoadError(error.message);
      showToastMessage("Error loading document preview");
    } finally {
      setIsLoadingDocx(false);
      setProcessingMessage("");
    }
  };

  // Handle view mode change
  const handleViewModeChange = (e) => {
    const newMode = e.target.value;
    console.log("Changing view mode to:", newMode);
    setViewMode(newMode);
    
    // Load DOCX if switching to docx mode and not loaded
    if ((newMode === "docx" || newMode === "split-docx") && !embedUrl && questionnaireFileId) {
      loadDocxPreview();
    }
  };

  // Handle draggable divider for DOCX split view
  const handleDocxMouseDown = (e) => {
    setIsDocxDragging(true);
    e.preventDefault();
  };

  const handleDocxMouseMove = useCallback(
    (e) => {
      if (!isDocxDragging || !docxContainerRef.current) return;
      const containerRect = docxContainerRef.current.getBoundingClientRect();
      const newRatio = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setDocxSplitRatio(Math.max(30, Math.min(70, newRatio)));
    },
    [isDocxDragging]
  );

  const handleDocxMouseUp = () => {
    setIsDocxDragging(false);
  };

  useEffect(() => {
    if (isDocxDragging) {
      document.addEventListener("mousemove", handleDocxMouseMove);
      document.addEventListener("mouseup", handleDocxMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleDocxMouseMove);
        document.removeEventListener("mouseup", handleDocxMouseUp);
      };
    }
  }, [isDocxDragging, handleDocxMouseMove]);

  // Check if DOCX is available
  const isDocxAvailable = questionnaireFileId && surveyFileName?.toLowerCase().endsWith(".docx");

  // ─────────────────────────────────────────────────────────────
  // Badge helper
  // ─────────────────────────────────────────────────────────────
  const badgeStyle = (bg, color) => ({
    fontSize: "0.75rem",
    padding: "3px 8px",
    backgroundColor: bg,
    color,
    borderRadius: "4px",
    fontWeight: 600,
    display: "inline-block",
  });

  const renderRowBadges = (row) => {
    if (!row) return null;
    const badges = [];

    if (row.screenout) badges.push(<span key="so" style={badgeStyle("#fee2e2", "#991b1b")}>Screen Out</span>);
    if (row.exclusive) badges.push(<span key="ex" style={badgeStyle("#dbeafe", "#1e40af")}>Exclusive</span>);
    if (row.other)     badges.push(<span key="ot" style={badgeStyle("#fef3c7", "#92400e")}>Other (specify)</span>);
    if (row.fix)       badges.push(<span key="fx" style={badgeStyle("#e0e7ff", "#3730a3")}>Fixed</span>);
    if (row.quotaCounts) badges.push(<span key="qt" style={badgeStyle("#f3e8ff", "#6b21a8")}>Quota: {row.quotaCounts}</span>);
    if (row.punchLogic)  badges.push(<span key="pl" style={badgeStyle("#ecfdf5", "#065f46")}>Punch: {row.punchLogic}</span>);
    if (row.filterLogic) badges.push(<span key="fl" style={badgeStyle("#f3e8ff", "#6b21a8")}>Filter: {row.filterLogic}</span>);

    return badges.length > 0 ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>{badges}</div>
    ) : null;
  };

  // ─────────────────────────────────────────────────────────────
  // Respondent View (Survey)
  // ─────────────────────────────────────────────────────────────
  const renderSurveyView = (q) => {
    if (!q) return <div style={{ color: "#6b7280", padding: "2rem" }}>No question data</div>;

    const titleHtml = { __html: q.Qtitle || "No question text" };
    const type = q.type || "Unknown";

    if (type === "Intro") {
      return (
        <div style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
          <div style={{ fontSize: "1.25rem", marginBottom: "2rem" }} dangerouslySetInnerHTML={titleHtml} />
          <button style={{ padding: "12px 40px", background: "#2563eb", color: "white", border: "none", borderRadius: "6px", fontSize: "1rem" }}>
            CONTINUE
          </button>
        </div>
      );
    }

    if (type === "Single Response" && q.rows?.length > 0) {
      return (
        <div>
          <div style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1.6rem" }} dangerouslySetInnerHTML={titleHtml} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {q.rows.map((row) => (
              <label
                key={row.index}
                style={{
                  padding: "12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  background: "#f9fafb",
                  fontSize: "0.98rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <input type="radio" readOnly style={{ width: "18px", height: "18px" }} />
                  <span dangerouslySetInnerHTML={{ __html: row.text || "—" }} />
                </div>
                {renderRowBadges(row)}
                {row.other && (
                  <input
                    type="text"
                    placeholder="Please specify..."
                    readOnly
                    style={{ marginTop: "8px", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.95rem", width: "100%" }}
                  />
                )}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (type === "Numeric") {
      const oe = q.oeparameters || {};
      return (
        <div>
          <div style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1.6rem" }} dangerouslySetInnerHTML={titleHtml} />
          <input
            type="number"
            min={oe.min}
            max={oe.max}
            placeholder="Enter your answer"
            readOnly
            style={{ width: "200px", padding: "10px 14px", fontSize: "1rem", border: "1px solid #d1d5db", borderRadius: "6px" }}
          />
        </div>
      );
    }

    if (q.rows?.length > 0 && q.columns?.length > 0) {
      const isMulti = type.includes("Multi") || type === "Multi Grid";
      return (
        <div>
          <div style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1.6rem" }} dangerouslySetInnerHTML={titleHtml} />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", border: "1px solid #d1d5db", minWidth: "400px", fontWeight: 600, fontSize: "0.95rem" }}>
                    Statement / Item
                  </th>
                  {q.columns.map((col) => (
                    <th
                      key={col.index}
                      style={{ padding: "12px 14px", textAlign: "center", border: "1px solid #d1d5db", minWidth: "130px", fontWeight: 600, fontSize: "0.9rem", whiteSpace: "normal" }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: col.text || "—" }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.rows.map((row, i) => (
                  <tr key={row.index || i} style={{ background: i % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                    <td style={{ padding: "12px 16px", border: "1px solid #d1d5db", fontSize: "0.98rem" }}>
                      <div style={{ whiteSpace: "normal", wordBreak: "break-word", marginBottom: "6px" }} dangerouslySetInnerHTML={{ __html: row.text || "—" }} />
                      {renderRowBadges(row)}
                    </td>
                    {q.columns.map((col) => (
                      <td key={col.index} style={{ textAlign: "center", padding: "12px", border: "1px solid #d1d5db" }}>
                        <input type={isMulti ? "checkbox" : "radio"} readOnly style={{ width: "18px", height: "18px" }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div style={{ color: "#6b7280", padding: "2rem 0" }}>
        <div style={{ fontSize: "1.25rem" }} dangerouslySetInnerHTML={titleHtml} />
        <p style={{ marginTop: "1rem" }}>(No preview for type: {type})</p>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Questionnaire / Metadata View (FIXED WIDTH)
  // ─────────────────────────────────────────────────────────────
  const renderQuestionnaireView = (q) => {
    if (!q) return <div>No question selected</div>;

    return (
      <div style={{ 
        fontSize: "0.95rem", 
        lineHeight: 1.5,
        height: "100%",
        overflowY: "auto",
        padding: "20px"
      }}>
        <h3 style={{ marginTop: 0, fontSize: "1.25rem", color: "#374151", marginBottom: "16px" }}>
          {q.Question_number || "—"} — {q.type || "Unknown"}
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <strong>Title:</strong>
          <div style={{ marginTop: "8px", padding: "12px", background: "#ffffff", borderRadius: "6px", border: "1px solid #e5e7eb" }} dangerouslySetInnerHTML={{ __html: q.Qtitle || "—" }} />
        </div>

        {q.instructions && (
          <div style={{ marginBottom: "20px" }}>
            <strong>Instructions:</strong>
            <div style={{ marginTop: "8px", padding: "12px", background: "#ffffff", borderRadius: "6px", border: "1px solid #e5e7eb" }} dangerouslySetInnerHTML={{ __html: q.instructions }} />
          </div>
        )}

        {q.logics && (
          <div style={{ marginBottom: "20px", padding: "12px", background: "#f3f4f6", borderRadius: "6px" }}>
            <strong>Logic:</strong> {q.logics}
          </div>
        )}

        {q.oeparameters && Object.keys(q.oeparameters).length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <strong>OE Parameters:</strong>
            <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "8px" }}>
              {Object.entries(q.oeparameters).map(([k, v]) => (
                <div key={k} style={{ padding: "8px", background: "#f3f4f6", borderRadius: "4px" }}>
                  <span style={{ fontWeight: 500, color: "#4b5563" }}>{k}:</span>{" "}
                  <span style={{ color: "#1f2937" }}>{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {q.rows?.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <strong>Rows ({q.rows.length}):</strong>
            <div style={{ marginTop: "8px", maxHeight: "300px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "6px" }}>
              {q.rows.map((row, i) => (
                <div key={row.index || i} style={{ padding: "12px", borderBottom: i < q.rows.length - 1 ? "1px solid #e5e7eb" : "none", background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <span style={{ fontWeight: 600, minWidth: "30px", color: "#4b5563" }}>{row.index || i + 1}.</span>
                    <div style={{ flex: 1 }}>
                      <div dangerouslySetInnerHTML={{ __html: row.text || "—" }} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                        {row.screenout && <span style={badgeStyle("#fee2e2", "#991b1b")}>Screen Out</span>}
                        {row.exclusive && <span style={badgeStyle("#dbeafe", "#1e40af")}>Exclusive</span>}
                        {row.other && <span style={badgeStyle("#fef3c7", "#92400e")}>Other</span>}
                        {row.fix && <span style={badgeStyle("#e0e7ff", "#3730a3")}>Fixed</span>}
                        {row.quotaCounts && <span style={badgeStyle("#f3e8ff", "#6b21a8")}>Quota: {row.quotaCounts}</span>}
                        {row.punchLogic && <span style={badgeStyle("#ecfdf5", "#065f46")}>Punch: {row.punchLogic}</span>}
                        {row.filterLogic && <span style={badgeStyle("#f3e8ff", "#6b21a8")}>Filter: {row.filterLogic}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {q.columns?.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <strong>Columns ({q.columns.length}):</strong>
            <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px" }}>
              {q.columns.map((col, i) => (
                <div key={col.index || i} style={{ padding: "8px", background: "#f3f4f6", borderRadius: "4px" }}>
                  <span style={{ fontWeight: 600, marginRight: "4px" }}>{col.index || i + 1}.</span>
                  <span dangerouslySetInnerHTML={{ __html: col.text || "—" }} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: "20px", padding: "12px", background: "#f3f4f6", borderRadius: "6px", color: "#4b5563", fontSize: "0.9rem" }}>
          {q.rowsRandomisation && <div>• Rows randomized</div>}
          {q.columnsRandomisation && <div>• Columns randomized</div>}
          {q.rowsRotation && <div>• Rows rotated</div>}
          {q.columnsRotation && <div>• Columns rotated</div>}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // JSON View
  // ─────────────────────────────────────────────────────────────
  const renderJsonView = (q) => {
    return (
      <div style={{ fontSize: "0.9rem", background: "#f8f9fa", padding: "16px", borderRadius: "8px", overflow: "auto", height: "100%" }}>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
          {JSON.stringify(q, null, 2)}
        </pre>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // DOCX View
  // ─────────────────────────────────────────────────────────────
  const renderDocxView = () => {
    if (!isDocxAvailable) {
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#6b7280", flexDirection: "column", gap: "16px" }}>
          <div style={{ fontSize: "16px" }}>No Word document available for this survey</div>
        </div>
      );
    }

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, overflow: "hidden", background: "#f9fafb" }}>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              title="Document Preview"
              style={{ border: "none" }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#6b7280",
                flexDirection: "column",
                gap: "16px"
              }}
            >
              {isLoadingDocx ? (
                <>
                  <div style={{ fontSize: "16px" }}>Loading document...</div>
                  <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                </>
              ) : docxLoadError ? (
                <>
                  <div style={{ fontSize: "16px", color: "#ef4444" }}>Error loading document: {docxLoadError}</div>
                  <button
                    onClick={loadDocxPreview}
                    style={{
                      padding: "8px 16px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer"
                    }}
                  >
                    Retry Loading
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: "16px" }}>Click Load Document to view</div>
                  <button
                    onClick={loadDocxPreview}
                    style={{
                      padding: "8px 16px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer"
                    }}
                  >
                    📄 Load Document
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Split View with Survey + DOCX
  // ─────────────────────────────────────────────────────────────
  const renderSplitWithDocx = () => {
    if (!isDocxAvailable) {
      return (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
          No Word document available for this survey
        </div>
      );
    }

    return (
      <div ref={docxContainerRef} style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
        {/* Left Panel - Survey - FIXED WIDTH BASED ON RATIO */}
        <div style={{ 
          width: `${docxSplitRatio}%`, 
          overflowY: "auto", 
          padding: "20px", 
          background: "#ffffff",
          transition: isDocxDragging ? "none" : "width 0.3s ease"
        }}>
          {renderSurveyView(currentQuestion)}
        </div>

        {/* Draggable Divider */}
        <div
          onMouseDown={handleDocxMouseDown}
          style={{
            width: "5px",
            cursor: "col-resize",
            backgroundColor: "#e5e7eb",
            position: "relative",
            flexShrink: 0,
            transition: isDocxDragging ? "none" : "background-color 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#3b82f6";
          }}
          onMouseLeave={(e) => {
            if (!isDocxDragging) {
              e.currentTarget.style.backgroundColor = "#e5e7eb";
            }
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "20px",
              height: "40px",
              backgroundColor: isDocxDragging ? "#3b82f6" : "#9ca3af",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
            }}
          >
            ⋮
          </div>
        </div>

        {/* Right Panel - DOCX - FIXED WIDTH BASED ON RATIO */}
        <div style={{ 
          width: `${100 - docxSplitRatio}%`, 
          backgroundColor: "#f9fafb",
          display: "flex",
          flexDirection: "column",
          transition: isDocxDragging ? "none" : "width 0.3s ease",
          position: "relative"
        }}>
          {isDocxDragging && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                cursor: "col-resize",
              }}
            />
          )}
          <div style={{ flex: 1, overflow: "hidden" }}>
            {embedUrl ? (
              <iframe
                src={embedUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                title="Document Preview"
                style={{ border: "none" }}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "#6b7280",
                  flexDirection: "column",
                  gap: "12px"
                }}
              >
                {isLoadingDocx ? (
                  <>
                    <div>Loading document...</div>
                    <div style={{ width: "30px", height: "30px", border: "2px solid #e5e7eb", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  </>
                ) : (
                  <>
                    <div>No document loaded</div>
                    <button
                      onClick={loadDocxPreview}
                      style={{
                        padding: "6px 12px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      Load Document
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Split View with Survey + Questionnaire (FIXED WIDTHS)
  // ─────────────────────────────────────────────────────────────
  const renderSplitView = () => {
    return (
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left Panel - Survey - FIXED WIDTH */}
        <div style={{ 
          width: `${splitRatio}%`, 
          overflowY: "auto", 
          padding: "20px", 
          background: "#ffffff",
          borderRight: "1px solid #e2e8f0",
          transition: isDragging ? "none" : "width 0.3s ease"
        }}>
          {renderSurveyView(currentQuestion)}
        </div>

        {/* Draggable Divider */}
        <div
          style={{
            width: "8px",
            background: isDragging ? "#9ca3af" : "#e2e8f0",
            cursor: "col-resize",
            userSelect: "none",
            flexShrink: 0,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
            const startX = e.clientX;
            const startRatio = splitRatio;

            const onMove = (ev) => {
              if (!isDragging) return;
              const delta = ev.clientX - startX;
              const containerWidth = window.innerWidth * 0.96;
              const newRatio = Math.max(30, Math.min(70, startRatio + (delta / containerWidth) * 100));
              setSplitRatio(newRatio);
            };

            const onUp = () => {
              setIsDragging(false);
              document.removeEventListener("mousemove", onMove);
              document.removeEventListener("mouseup", onUp);
            };

            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
          }}
        />

        {/* Right Panel - Questionnaire - FIXED WIDTH */}
        <div style={{ 
          width: `${100 - splitRatio}%`, 
          overflowY: "auto", 
          background: "#f9fafb",
          transition: isDragging ? "none" : "width 0.3s ease"
        }}>
          {renderQuestionnaireView(currentQuestion)}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Render based on viewMode
  // ─────────────────────────────────────────────────────────────
  const renderContent = () => {
    console.log("Rendering content with viewMode:", viewMode);
    
    if (viewMode === "survey") {
      return (
        <div style={{ height: "100%", overflowY: "auto", padding: "20px", background: "#ffffff" }}>
          {renderSurveyView(currentQuestion)}
        </div>
      );
    }

    if (viewMode === "questionnaire") {
      return (
        <div style={{ height: "100%", overflowY: "auto", background: "#f9fafb" }}>
          {renderQuestionnaireView(currentQuestion)}
        </div>
      );
    }

    if (viewMode === "json") {
      return (
        <div style={{ height: "100%", overflowY: "auto", padding: "20px", background: "#ffffff" }}>
          {renderJsonView(currentQuestion)}
        </div>
      );
    }

    if (viewMode === "docx") {
      return renderDocxView();
    }

    if (viewMode === "split-docx") {
      return renderSplitWithDocx();
    }

    // Default split view (Survey + Questionnaire)
    return renderSplitView();
  };

  return (
    <>
      {/* This is the button to open the preview modal */}
      <CommonButton
        label="Quick Preview"
        title="Preview question + metadata + navigation"
        onClick={() => {
          setCurrentIndex(initialIndex);
          setIsPreviewOpen(true);
        }}
      />

      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => setIsPreviewOpen(false)}
        style={{
          overlay: { backgroundColor: "rgba(0,0,0,0.65)", zIndex: 1300 },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "min(96vw, 1600px)",
            height: "92vh",
            borderRadius: "16px",
            padding: 0,
            border: "none",
            overflow: "hidden",
          },
        }}
      >
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div
            style={{
              padding: "14px 24px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: "1.4rem", color: "#1e293b" }}>
                Question Preview — {currentQuestion?.Question_number || "?"}
                <span style={{ fontSize: "0.95rem", color: "#64748b", marginLeft: "12px" }}>
                  ({currentIndex + 1} / {allQuestions.length})
                </span>
              </h2>

              {/* View Mode Dropdown - Enhanced with DOCX options */}
              <select
                value={viewMode}
                onChange={handleViewModeChange}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "0.95rem",
                  background: "white",
                  minWidth: "280px",
                  cursor: "pointer",
                }}
              >
                <optgroup label="Standard Views">
                  <option value="split">Split View (Survey + Questionnaire)</option>
                  <option value="survey">Survey Only</option>
                  <option value="questionnaire">Questionnaire Only</option>
                  <option value="json">JSON View</option>
                </optgroup>
                
                {/* DOCX Options - Only show if DOCX file exists */}
                {isDocxAvailable && (
                  <optgroup label="Document Views">
                    <option value="split-docx">Split View (Survey + Word Doc)</option>
                    <option value="docx">Word Document Only</option>
                  </optgroup>
                )}
              </select>

              {/* Document Status Indicator */}
              {isDocxAvailable && (
                <div style={{ 
                  fontSize: "0.85rem", 
                  color: embedUrl ? "#10b981" : (isLoadingDocx ? "#f59e0b" : "#6b7280"),
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>📄</span>
                  {embedUrl ? "Document loaded" : (isLoadingDocx ? "Loading..." : "Document ready")}
                </div>
              )}
            </div>

            <button
              onClick={() => setIsPreviewOpen(false)}
              style={{ background: "none", border: "none", fontSize: "2rem", cursor: "pointer", color: "#64748b", lineHeight: 1, padding: "0 8px" }}
            >
              ×
            </button>
          </div>

          {/* Main content area - FIXED HEIGHT */}
          <div ref={containerRef} style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
            {renderContent()}
          </div>

          {/* Navigation - Only show for survey-related views */}
          {(viewMode === "survey" || viewMode === "split" || viewMode === "split-docx") && (
            <div
              style={{
                padding: "12px 24px",
                borderTop: "1px solid #e2e8f0",
                background: "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <button
                onClick={handleBack}
                disabled={isFirst}
                style={{
                  padding: "10px 28px",
                  background: isFirst ? "#e2e8f0" : "#64748b",
                  color: isFirst ? "#94a3b8" : "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: isFirst ? "not-allowed" : "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                ← Back
              </button>

              <button
                onClick={handleNext}
                disabled={isLast}
                style={{
                  padding: "10px 36px",
                  background: isLast ? "#10b981" : "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: isLast ? "default" : "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                {isLast ? "End of Survey" : "Next →"}
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Global styles for animations */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default QuestionPreview;